/* eslint-disable no-restricted-properties */
import { usePageContext } from '#app/hooks/usePageContext.js';
import { useRouter } from '#app/hooks/useRouter.js';
import {
  deserializeQueryParameters,
  type OutputRoute,
  type OutputRoutes,
  type Routes,
  routes,
  serializeQueryParameter,
} from '@contra/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

const UPDATE_EVENT_NAME = 'useURLParameters:updated';

const createUpdateEvent = () => {
  return new Event(UPDATE_EVENT_NAME);
};

type RouteParameters<T extends OutputRoute> = Exclude<
  OutputRoutes[T]['search'],
  undefined
>;

export const useURLParameters = <T extends OutputRoute>(
  route: T,
): [
  RouteParameters<T>,
  (newParameters: Partial<Routes[T]['search']>) => void,
] => {
  const router = useRouter();
  const { urlParsed } = usePageContext();
  // We fall back to the page context if we are on the server
  const [search, setSearch] = useState(
    typeof window === 'undefined'
      ? (urlParsed.searchOriginal ?? '')
      : window.location.search,
  );

  const parameters = useMemo(() => {
    let parametersObject = {} as RouteParameters<T>;

    const searchParameters = new URLSearchParams(search);
    const originalSchema = routes[route].shape.search;
    const unwrappedSchema =
      originalSchema instanceof z.ZodOptional
        ? originalSchema.unwrap()
        : originalSchema;

    if (unwrappedSchema instanceof z.ZodObject) {
      parametersObject = deserializeQueryParameters(
        unwrappedSchema,
        searchParameters,
      ) as RouteParameters<T>;
    }

    return parametersObject;
  }, [route, search]);

  const setParameters = useCallback(
    (newParameters: Partial<Routes[T]['search']>) => {
      const searchParameters = new URLSearchParams(window.location.search);

      for (const [key, value] of Object.entries(newParameters)) {
        if (value === undefined) {
          searchParameters.delete(key);
        } else {
          searchParameters.set(key, serializeQueryParameter(value));
        }
      }

      const stringifiedParameters = searchParameters.toString();
      const newSearch = stringifiedParameters
        ? `?${stringifiedParameters}`
        : '';
      const newUrl = `${window.location.pathname}${newSearch}`;

      window.history.replaceState({}, '', newUrl);

      window.dispatchEvent(createUpdateEvent());
    },
    [],
  );

  // Note: we are NOT leveraging a MutationObserver here due to potential performance concerns.
  // Similarly, we are not leveraging navigation changes here to prevent unnecessary re-renders.
  // There is no equivalent built-in event we could listen to, which is why we are using a custom event
  // (see https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event#the_history_stack).
  // We should still capture all relevant URL search parameter changes as long as we are consistently
  // using this hook everywhere.
  useEffect(() => {
    const listener = () => {
      setSearch(window.location.search);
    };

    const unsubscribe = router.subscribe({
      onTransitionEnd: listener,
    });

    window.addEventListener(UPDATE_EVENT_NAME, listener);

    return () => {
      window.removeEventListener(UPDATE_EVENT_NAME, listener);
      unsubscribe();
    };
  }, [route, router]);

  return [parameters, setParameters];
};
